import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  Loader2,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  User,
  Headphones,
  Lock,
  XCircle,
  Bot,
  UserRound,
  Paperclip,
  X,
  Image,
  FileText,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";

interface SupportMessage {
  id: string;
  ticketId: string;
  senderType: string;
  senderId: string | null;
  senderName: string;
  message: string;
  isInternal: boolean;
  attachments?: string[];
  createdAt: string;
}

interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  escalated: boolean;
  escalatedAt: string | null;
  hasAISupport: boolean;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  user: { firstName: string; lastName: string; email: string };
  organization: { name: string };
  messages: SupportMessage[];
}

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; icon: React.ReactNode }
> = {
  open: {
    label: "Abierto",
    bg: "bg-blue-100",
    text: "text-blue-700",
    icon: <Clock className="w-4 h-4" />,
  },
  in_progress: {
    label: "En progreso",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: <Loader2 className="w-4 h-4" />,
  },
  waiting_reply: {
    label: "Esperando respuesta",
    bg: "bg-purple-100",
    text: "text-purple-700",
    icon: <MessageSquare className="w-4 h-4" />,
  },
  resolved: {
    label: "Resuelto",
    bg: "bg-green-100",
    text: "text-green-700",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  closed: {
    label: "Cerrado",
    bg: "bg-gray-100",
    text: "text-gray-700",
    icon: <Lock className="w-4 h-4" />,
  },
};

const CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  billing: "Facturación",
  technical: "Técnico",
  feature_request: "Solicitud de mejora",
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Baja", color: "text-gray-500" },
  normal: { label: "Normal", color: "text-blue-500" },
  high: { label: "Alta", color: "text-orange-500" },
  urgent: { label: "Urgente", color: "text-red-500" },
};

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isOwner = authUser?.role === "owner";

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Reply form
  const [replyMessage, setReplyMessage] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Status change
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isEscalating, setIsEscalating] = useState(false);
  const [isWaitingBot, setIsWaitingBot] = useState(false);

  useEffect(() => {
    if (id) fetchTicket();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  // Poll for new bot responses after sending a message
  useEffect(() => {
    if (!isWaitingBot || !id) return;
    const interval = setInterval(async () => {
      try {
        const response = await apiClient.get(`/support-tickets/${id}`);
        const data = response.data.data;
        const lastMsg = data.messages[data.messages.length - 1];
        if (lastMsg && lastMsg.senderType !== "user") {
          setTicket(data);
          setIsWaitingBot(false);
        }
      } catch {
        /* ignore polling errors */
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [isWaitingBot, id]);

  const fetchTicket = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/support-tickets/${id}`);
      setTicket(response.data.data);
    } catch {
      setError("Error al cargar el ticket");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setIsSending(true);
    try {
      // 1. Upload pending files first
      let attachmentUrls: string[] = [];
      if (pendingFiles.length > 0) {
        setIsUploadingFiles(true);
        const uploadPromises = pendingFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          const response = await apiClient.post(
            `/support-tickets/${id}/upload`,
            {
              file: base64,
              filename: file.name,
            },
          );
          return response.data.data.url as string;
        });
        attachmentUrls = await Promise.all(uploadPromises);
        setIsUploadingFiles(false);
      }

      // 2. Send reply with attachment URLs
      await apiClient.post(`/support-tickets/${id}/reply`, {
        message: replyMessage,
        isInternal,
        attachments: attachmentUrls,
      });
      setReplyMessage("");
      setIsInternal(false);
      setPendingFiles([]);
      await fetchTicket();
      // If user sent a message, plan has AI, and ticket is not escalated, wait for bot response
      if (!isOwner && ticket?.hasAISupport && !ticket?.escalated) {
        setIsWaitingBot(true);
      }
    } catch {
      setError("Error al enviar la respuesta");
    } finally {
      setIsSending(false);
      setIsUploadingFiles(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const allowed = files.filter((f) => {
      const isValidType =
        f.type.startsWith("image/") || f.type === "application/pdf";
      const isValidSize = f.size <= 5 * 1024 * 1024;
      return isValidType && isValidSize;
    });
    setPendingFiles((prev) => {
      const combined = [...prev, ...allowed];
      return combined.slice(0, 5); // max 5 files
    });
    // reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      await apiClient.patch(`/support-tickets/${id}/status`, {
        status: newStatus,
      });
      fetchTicket();
    } catch {
      setError("Error al actualizar el estado");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleEscalate = async () => {
    setIsEscalating(true);
    try {
      await apiClient.post(`/support-tickets/${id}/escalate`);
      setIsWaitingBot(false);
      await fetchTicket();
    } catch {
      setError("Error al escalar el ticket");
    } finally {
      setIsEscalating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const goBack = () => {
    if (isOwner) {
      navigate("/admin/tickets");
    } else {
      navigate("/help");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{error || "Ticket no encontrado"}</p>
          <button
            onClick={goBack}
            className="mt-4 text-primary-600 font-medium hover:underline"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priorityInfo =
    PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
  const isClosed = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4 flex-shrink-0">
        <div className="flex items-start gap-3">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 mt-0.5"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-mono text-gray-400">
                #{ticket.ticketNumber}
              </span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}
              >
                {statusInfo.icon}
                {statusInfo.label}
              </span>
              <span className={`text-xs font-medium ${priorityInfo.color}`}>
                {priorityInfo.label}
              </span>
            </div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
              {ticket.subject}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              <span>{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
              <span>·</span>
              {isOwner && (
                <>
                  <span>{ticket.organization.name}</span>
                  <span>·</span>
                  <span>
                    {ticket.user.firstName} {ticket.user.lastName}
                  </span>
                  <span>·</span>
                </>
              )}
              <span>{formatDate(ticket.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Status actions */}
        {isOwner && (
          <div className="flex flex-wrap items-center gap-2 mt-3 ml-11">
            {["open", "in_progress", "waiting_reply", "resolved", "closed"].map(
              (s) => {
                const sInfo = STATUS_CONFIG[s];
                return (
                  <button
                    key={s}
                    disabled={ticket.status === s || isUpdatingStatus}
                    onClick={() => handleStatusChange(s)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      ticket.status === s
                        ? `${sInfo.bg} ${sInfo.text} ring-2 ring-offset-1 ring-current`
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    } disabled:opacity-50`}
                  >
                    {sInfo.label}
                  </button>
                );
              },
            )}
          </div>
        )}

        {/* User status controls (close/reopen) */}
        {!isOwner && (
          <div className="flex items-center gap-2 mt-3 ml-11">
            {!isClosed ? (
              <button
                onClick={() => handleStatusChange("closed")}
                disabled={isUpdatingStatus}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                <XCircle className="w-3.5 h-3.5" />
                Cerrar ticket
              </button>
            ) : (
              <button
                onClick={() => handleStatusChange("open")}
                disabled={isUpdatingStatus}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Reabrir ticket
              </button>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {ticket.messages.map((msg) => {
          const isAdmin = msg.senderType === "admin";
          const isBot = msg.senderType === "bot";
          const isLeft = isAdmin || isBot;
          return (
            <div
              key={msg.id}
              className={`flex ${isLeft ? "justify-start" : "justify-end"}`}
            >
              <div
                className={`max-w-[85%] md:max-w-[70%] ${msg.isInternal ? "opacity-70" : ""}`}
              >
                <div
                  className={`flex items-center gap-2 mb-1 ${isLeft ? "" : "flex-row-reverse"}`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isBot
                        ? "bg-emerald-100"
                        : isAdmin
                          ? "bg-primary-100"
                          : "bg-gray-100"
                    }`}
                  >
                    {isBot ? (
                      <Bot className="w-3.5 h-3.5 text-emerald-600" />
                    ) : isAdmin ? (
                      <Headphones className="w-3.5 h-3.5 text-primary-600" />
                    ) : (
                      <User className="w-3.5 h-3.5 text-gray-600" />
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    {msg.senderName}
                  </span>
                  {isBot && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                      IA
                    </span>
                  )}
                  {msg.isInternal && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                      Nota interna
                    </span>
                  )}
                </div>
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    msg.isInternal
                      ? "bg-amber-50 border border-amber-200 text-amber-900"
                      : isBot
                        ? "bg-emerald-50 border border-emerald-200 text-gray-800 rounded-tl-md"
                        : isAdmin
                          ? "bg-white border border-gray-200 text-gray-800 rounded-tl-md"
                          : "bg-primary-600 text-white rounded-tr-md"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.message}</p>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-2 space-y-1 border-t border-black/10 pt-2">
                      {msg.attachments.map((url, i) => {
                        const filename =
                          url.split("/").pop()?.split("?")[0] ||
                          `adjunto-${i + 1}`;
                        const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(
                          filename,
                        );
                        return (
                          <div key={i} className="flex items-center gap-1.5">
                            {isImage ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={url}
                                  alt={filename}
                                  className="max-w-[180px] max-h-[120px] rounded-lg border border-black/10 object-cover"
                                />
                              </a>
                            ) : (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs underline opacity-80 hover:opacity-100"
                              >
                                <FileText className="w-3 h-3 flex-shrink-0" />
                                {filename}
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p
                  className={`text-xs text-gray-400 mt-1 ${isLeft ? "" : "text-right"}`}
                >
                  {formatDate(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}

        {/* Bot typing indicator */}
        {isWaitingBot && (
          <div className="flex justify-start">
            <div className="max-w-[85%] md:max-w-[70%]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-100">
                  <Bot className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-gray-600">
                  Asistente RM
                </span>
              </div>
              <div className="px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 rounded-tl-md">
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                  <span className="text-xs text-emerald-600 ml-2">
                    Escribiendo...
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Reply form */}
      {!isClosed ? (
        <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
          {/* Escalate button for non-owner, non-escalated tickets with AI support */}
          {!isOwner && ticket.hasAISupport && !ticket.escalated && (
            <div className="flex items-center justify-center mb-3">
              <button
                onClick={handleEscalate}
                disabled={isEscalating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
              >
                {isEscalating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserRound className="w-4 h-4" />
                )}
                Hablar con un agente
              </button>
            </div>
          )}
          {/* Escalated indicator */}
          {ticket.escalated && ticket.hasAISupport && !isOwner && (
            <div className="flex items-center gap-2 mb-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">
              <Headphones className="w-3.5 h-3.5" />
              Un agente de soporte atenderá tu consulta
            </div>
          )}
          {isOwner && (
            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-amber-700 font-medium">Nota interna</span>
              </label>
              <span className="text-xs text-gray-400">
                (no visible para el cliente)
              </span>
            </div>
          )}
          <form onSubmit={handleReply} className="flex flex-col gap-2">
            {/* Pending files preview */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                {pendingFiles.map((file, i) => {
                  const isImage = file.type.startsWith("image/");
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-700"
                    >
                      {isImage ? (
                        <Image className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      ) : (
                        <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      )}
                      <span className="max-w-[120px] truncate">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="ml-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder={
                  isInternal
                    ? "Escribe una nota interna..."
                    : "Escribe tu respuesta..."
                }
                rows={4}
                className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:border-primary-500 resize-none ${
                  isInternal
                    ? "border-amber-300 focus:ring-amber-500 bg-amber-50"
                    : "border-gray-300 focus:ring-primary-500"
                }`}
                maxLength={5000}
              />
              <div className="flex flex-col gap-1 self-end">
                {/* Attach file button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending || pendingFiles.length >= 5}
                  title="Adjuntar archivo (máx. 5 MB, imágenes o PDF)"
                  className="px-3 py-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                {/* Send button */}
                <button
                  type="submit"
                  disabled={
                    isSending || isUploadingFiles || !replyMessage.trim()
                  }
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isInternal
                      ? "bg-amber-500 text-white hover:bg-amber-600"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {isSending || isUploadingFiles ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </form>
        </div>
      ) : (
        <div className="bg-gray-50 border-t border-gray-200 p-4 text-center flex-shrink-0">
          <p className="text-sm text-gray-500">
            Este ticket está{" "}
            {ticket.status === "resolved" ? "resuelto" : "cerrado"}.{" "}
            <button
              onClick={() => handleStatusChange("open")}
              className="text-primary-600 font-medium hover:underline"
            >
              Reabrir
            </button>
          </p>
        </div>
      )}
    </div>
  );
}
